/** Plain-language explainer copy for every module's Guide tab.
 *  One source of truth — edit copy here, not inside the page components.
 *  ALL copy must match the brand voice: plain English, no jargon, no hype.
 */

export const CHAT_GUIDE = {
  title: "AI Chat Assistant",
  whatItIs:
    "Your own AI assistant, like ChatGPT but inside your business. Ask it to write, summarize, brainstorm, analyze — anything you'd ask a smart helper to do. It can also look things up in documents you've uploaded.",
  capabilities: [
    { title: "Summarize long things", body: "Paste a long email, contract, or article. Get a 3-sentence summary." },
    { title: "Draft a tricky reply", body: "Describe the situation. Get a professional response in seconds." },
    { title: "Compare options", body: "Paste two vendor quotes or job descriptions side by side. Get the differences." },
    { title: "Brainstorm", body: "Names for a product, social post angles, talking points for a meeting." },
    { title: "Rewrite something", body: "Paste content in a different tone (more formal, friendlier, shorter)." },
  ],
  howTo: [
    "Click **\"+ New Conversation\"** on the left side.",
    "Type your question or paste the text you need help with.",
    "Hit Enter or click Send. The answer streams back in seconds.",
    "To use your uploaded documents, toggle **\"Use Knowledge Base\"** in the message box. The AI will check your docs first.",
    "Try different models at the top — currently powered by GPT-4o (Claude and Gemini are on the roadmap).",
  ],
  cta: "Got it — take me to AI Chat",
};

export const KB_GUIDE = {
  title: "Knowledge Base",
  whatItIs:
    "A place to upload your business documents — pricing sheets, policies, contracts, FAQs, training materials. Once uploaded, the AI can answer questions using YOUR information, not generic answers from the internet.",
  capabilities: [
    { title: "Accurate pricing answers", body: "Upload your pricing PDF. AI quotes correctly every time." },
    { title: "Self-serve onboarding", body: "Upload your onboarding doc. New hires ask AI instead of you." },
    { title: "Customer support", body: "Upload your refund policy. The website chatbot answers refund questions correctly." },
    { title: "Sales enablement", body: "Upload product specs. Sales reps get accurate answers in seconds." },
    { title: "Internal Q&A", body: "Upload your employee handbook. Anyone on your team can search it instantly." },
  ],
  howTo: [
    "Drag a PDF, Word document, or text file into the upload area (or click to browse).",
    "Wait about 30 seconds while we read and organize the file. Status changes from \"processing\" to \"ready\".",
    "Make sure the **\"Use in Chat\"** toggle is ON for documents you want the AI to use.",
    "Go to AI Chat. Ask a question that's answered in your document. The AI will use your file and show which document it pulled from.",
  ],
  cta: "Got it — take me to Knowledge Base",
};

export const LEADS_GUIDE = {
  title: "Smart Leads",
  whatItIs:
    "A simple list of your prospects — people who might buy from you. AI helps you score them, personalize outreach, and organize them through stages until they become customers.",
  capabilities: [
    { title: "Score 500 leads in 2 minutes", body: "Import a CSV, click \"AI Score\". Each lead gets a 1-100 fit score with a reason." },
    { title: "Personalized outreach in seconds", body: "Click \"Personalize\" on a lead. AI writes a tailored opener about their company." },
    { title: "Track your pipeline", body: "Move leads through stages (New → Contacted → Qualified → Won) so nothing slips through." },
    { title: "Focus on the best", body: "Filter for \"score above 80 + stage New\" to spend your time where it matters." },
    { title: "Search and find anyone", body: "Type a name or company in the search bar." },
  ],
  howTo: [
    "Click **\"Import CSV\"**. Drag your spreadsheet, map your columns (name, email, company), click Import. Done.",
    "Select a few leads with the checkboxes, click **\"AI Score\"**. Scores appear in a few seconds.",
    "Click any lead to open the detail drawer. Click **\"Personalize\"** to generate a custom opener.",
    "Drag leads between stages, or change the stage in the lead's detail view.",
    "Use the filters at the top to narrow your view (stage, score range, saved list, tags).",
  ],
  cta: "Got it — take me to Smart Leads",
};

export const SEQUENCES_GUIDE = {
  title: "Outreach Sequences",
  whatItIs:
    "Automated email follow-ups. You write the emails once. The system sends them on schedule and tracks who opens, clicks, and replies. Stops automatically if someone replies.",
  capabilities: [
    { title: "Cold outreach that follows up", body: "3 emails over 2 weeks, automatic. Stops when they reply." },
    { title: "New customer onboarding", body: "A welcome series with helpful tips over the first 30 days." },
    { title: "Re-engage quiet leads", body: "\"We've added something new\" campaign to old contacts." },
    { title: "After-demo follow-up", body: "Automatic check-ins until they say yes or no." },
    { title: "Real engagement data", body: "See who opened, clicked, replied, and bounced. (Powered by real Resend events.)" },
  ],
  howTo: [
    "Go to **Sequences** in the sidebar. Click **\"+ New Sequence\"**. Name it something simple.",
    "Add a step: choose **\"Email\"**. Write the subject and body. Use merge tags like `{{first_name}}` to personalize.",
    "Add a **\"Wait\"** step between emails (e.g., 3 days). Repeat for follow-ups.",
    "Click **\"AI Write\"** if you want help drafting an email. It generates a subject and body from a short prompt.",
    "Save the sequence, then click **\"Activate\"**.",
    "Go to **Leads**, select who you want to enroll, click **\"Enroll in sequence\"**. Done — emails start flowing.",
  ],
  cta: "Got it — back to Steps",
};

export const CONTENT_GUIDE = {
  title: "Content Engine",
  whatItIs:
    "AI writes content for you — blog posts, LinkedIn posts, emails, ads. You give it the topic and a few details; it gives you a draft.",
  capabilities: [
    { title: "Blog post in 2 minutes", body: "Give it a topic, target audience, tone, and length. Out comes a full article." },
    { title: "LinkedIn posts that don't sound robotic", body: "Pick an angle (story, insight, tip), get a post that fits." },
    { title: "Sales emails for a specific audience", body: "Tell it who you're emailing and what you want; get a draft." },
    { title: "Ad copy variations", body: "3 headlines + 2 descriptions to A/B test." },
    { title: "Save what works as templates", body: "Reuse settings for next time without re-entering them." },
  ],
  howTo: [
    "Pick a generator from the Generators tab (Blog, LinkedIn, Email, or Ad).",
    "Fill in the form: topic, audience, tone, any key points you want included.",
    "Click **\"Generate\"**. The output streams in.",
    "**Copy** to clipboard, **Save to History** to revisit later, or **Regenerate** if you want a different version.",
    "Click **\"Use as Template\"** to save your settings. Next time, start from your template instead of blank.",
  ],
  cta: "Got it — back to Generators",
};

export const AGENTS_GUIDE = {
  title: "AI Agents",
  whatItIs:
    "Customizable AI bots you can put on your website. They chat with visitors 24/7 — answering questions, qualifying leads, and booking appointments. No employee required.",
  capabilities: [
    { title: "Website chatbot", body: "Answers visitor questions using your Knowledge Base, even at 2am." },
    { title: "Lead qualifier", body: "Asks visitors about their needs and budget. Adds qualified ones straight to your Leads list." },
    { title: "FAQ support bot", body: "Handles common questions so you don't have to repeat yourself." },
    { title: "Appointment booker", body: "Captures preferred times from visitors so you can confirm later." },
    { title: "Always polite, always on-brand", body: "You set the tone and personality." },
  ],
  howTo: [
    "Click **\"+ New Agent\"**. Choose a type (Chatbot, Lead Qualifier, FAQ Bot, or Booker).",
    "Fill in the name, welcome message, and tone. Defaults are fine if you're not sure.",
    "Go to the **Knowledge** tab. Attach any documents you've uploaded so the agent can answer from them.",
    "Go to the **Embed** tab. Copy the small `<script>` snippet. Paste it before the `</body>` tag on your website.",
    "Or, share the public URL directly to test before you embed.",
    "Check the **Conversations** tab to see what visitors are asking.",
  ],
  cta: "Got it — back to Setup",
};

export const ASSISTANT_GUIDE = {
  title: "Executive Assistant",
  whatItIs:
    "An AI that quietly watches what's happening across your business — leads, sequences, agent conversations, content — and gives you daily summaries, suggested tasks, and recommendations. So nothing slips through.",
  capabilities: [
    { title: "Morning briefing", body: "A short summary of what changed yesterday, so you start the day informed." },
    { title: "Smart follow-up reminders", body: "\"Maya at QuantumLabs hasn't replied in 5 days. Want to send a follow-up?\"" },
    { title: "Strategic suggestions", body: "\"Your best sequence is X. Want to enroll these 12 leads in it?\"" },
    { title: "Quiet alerts", body: "Bounce rate up, sequence reply rate dropped, new agent conversations need review." },
    { title: "Email digests", body: "Get the summary in your inbox every morning if you want." },
  ],
  howTo: [
    "Open the Executive Assistant. First visit, click **\"Run now\"** to generate your first summary.",
    "Read the notifications and tasks that appear. Mark tasks complete as you work through them.",
    "Set the run frequency in **Settings** (every 6 hours, 12 hours, or daily).",
    "Turn on **\"Email me a daily summary\"** if you want it delivered to your inbox.",
    "Use the bell icon at the top of the app to see new notifications without leaving your current page.",
  ],
  cta: "Got it — show me notifications",
};

export const INTEGRATIONS_GUIDE = {
  title: "Integrations",
  whatItIs:
    "Connections to other tools you already use — Stripe for billing, Resend for email, plus HubSpot, Slack, Gmail, Calendar, and more as we add them. Everything talks to everything else.",
  capabilities: [
    { title: "Take real payments", body: "Stripe is connected. You can charge customers." },
    { title: "Real emails", body: "Resend is connected. Sequences and password resets go out for real." },
    { title: "Sync your CRM", body: "Once HubSpot is connected, your leads sync both ways automatically." },
    { title: "Get summaries in Slack", body: "Once Slack is connected, the Executive Assistant can post to your team channel." },
    { title: "Send from your Gmail", body: "Once Gmail is connected, sequences can send from your actual address." },
  ],
  howTo: [
    "Browse the integrations grid. They're grouped by type (CRM, Email, Communication, Productivity, Commerce).",
    "Find the one you want to connect. Click **\"Connect\"**.",
    "For live integrations (Stripe, Resend) — follow the authorization flow.",
    "For coming-soon integrations — join the waitlist with your email. We'll notify you when it's live.",
  ],
  cta: "Got it — back to the catalogue",
};

export const SETTINGS_GUIDE = {
  title: "Settings",
  whatItIs:
    "Profile is your personal account. Organization is your business. Team is for inviting collaborators. Billing manages your subscription. Most people only need to touch these when something changes.",
  capabilities: [
    { title: "Profile", body: "Change your name, email, password, or sign out of every device." },
    { title: "Organization", body: "Rename your workspace, see your trial / plan status." },
    { title: "Team", body: "Invite teammates by email. Manage roles. Remove people who've left." },
    { title: "Billing", body: "Upgrade, change card, cancel, or download invoices. We use Stripe — your card never touches our servers." },
  ],
  howTo: [
    "Click the tab for what you want to change.",
    "Make your edits, save. Changes take effect immediately.",
    "On Billing, click **\"Manage subscription\"** for self-serve card / invoice management via Stripe's portal.",
    "On Team, click **\"Invite teammate\"** — they'll get a real email with an accept link.",
  ],
  cta: "Got it — take me to Profile",
};

export const ADMIN_GUIDE = {
  title: "Admin",
  whatItIs:
    "This is the platform control panel — only visible to super admins. It's how you operate Symplee itself, not any individual workspace.",
  capabilities: [
    { title: "Tenants", body: "Lists every business using Symplee. Suspend, change plan, change seat count." },
    { title: "Users", body: "Manages individual people across all tenants. Ban / unban / impersonate." },
    { title: "Usage", body: "Shows what's getting used — orgs, users, sequences, AI events by model." },
    { title: "AI Models", body: "Turn specific models on/off platform-wide." },
    { title: "Feature Flags", body: "Toggle experimental features on or off." },
    { title: "Logs", body: "Your audit trail — every admin action is recorded." },
    { title: "Email", body: "Resend integration health, sent log, unsubscribes, and the engagement webhook." },
  ],
  howTo: [
    "Click any tab to see what's in it. None of these screens make changes until you press a button.",
    "Most actions are reversible. Bans and suspensions can be undone in one click.",
    "Impersonation lets you log in AS another user (great for support). A sticky banner shows when you're impersonating — click it to exit.",
    "Everything you do here writes a row to the audit log.",
  ],
  cta: "Got it — back to Tenants",
};
